import { useContext, useState } from 'react';
import { useCallback } from 'react';
import { HimmaContext } from '../context/HimmaContext';
import { 
    SendNewsletterRequest, 
    DeleteNewsletterSubscribersRequest,
    GetNewsletterSubscribersLimitEnum,
    GetNewsletterSubscribersStatusEnum,
    GetNewsletterSubscribersSortByEnum,
    GetNewsletterSubscribersSortOrderEnum,
    ToggleNewletterSubscriberStatusRequest
} from '../client-sdk';

export const useNewsletterApi = () => {
    const api = useContext(HimmaContext)!;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const getNewsletterSubscribers = useCallback(
        async (params: {
            page: number;
            limit: GetNewsletterSubscribersLimitEnum;
            search?: string;
            status?: GetNewsletterSubscribersStatusEnum;
            sortBy?: GetNewsletterSubscribersSortByEnum;
            sortOrder?: GetNewsletterSubscribersSortOrderEnum;
        }) => {
            setLoading(true);
            setError(null);
            try {
                const response = await api.getNewsletterSubscribers(
                    params.page,
                    params.limit,
                    params.search,
                    params.status,
                    params.sortBy,
                    params.sortOrder
                );
                return response.data;
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred while fetching newsletter subscribers');
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [api]
    );

    const sendNewsletter = useCallback(
        async (requestData: SendNewsletterRequest) => {
            setLoading(true);
            setError(null);
            try {
                const response = await api.sendNewsletter(requestData);
                return response.data;
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred while sending newsletter');
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [api]
    );

    const deleteNewsletterSubscribers = useCallback(
        async (requestData: DeleteNewsletterSubscribersRequest) => {
            setLoading(true);
            setError(null);
            try {
                const response = await api.deleteNewsletterSubscribers(requestData);
                return response.data;
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred while deleting newsletter subscribers');
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [api]
    );

    const toggleNewsletterSubscribersStatus = useCallback(
        async (requestData: ToggleNewletterSubscriberStatusRequest) => {
            setLoading(true);
            setError(null);
            try {
                const response = await api.toggleNewletterSubscriberStatus(requestData);
                return response.data;
            } catch (err) {
                setError(
                    err instanceof Error
                        ? err.message
                        : 'An error occurred while updating subscriber status'
                );
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [api]
    );

    return {
        getNewsletterSubscribers,
        sendNewsletter,
        deleteNewsletterSubscribers,
        toggleNewsletterSubscribersStatus,
        loading,
        error,
    };
};
